import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { localizedPath } from '../utils/routes';
import SEOHead from '../components/SEOHead';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?\d[\d\s\-().]{6,18}\d$/;

export default function Register() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', confirm: '' });
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { mc } = useMarketingCopy();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);

  const validate = () => {
    const errors = {};
    if (!form.first_name.trim()) errors.first_name = t.auth.register.firstNameLabel + ' required';
    else if (form.first_name.trim().length > 50) errors.first_name = t.auth.register.nameTooLong;

    if (!form.last_name.trim()) errors.last_name = t.auth.register.lastNameLabel + ' required';
    else if (form.last_name.trim().length > 50) errors.last_name = t.auth.register.nameTooLong;

    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) errors.email = t.auth.register.invalidEmail;

    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) errors.phone = t.auth.register.invalidPhone;

    if (form.password.length < 6) errors.password = t.auth.register.passwordTooShort;
    if (form.password !== form.confirm) errors.confirm = t.auth.register.passwordMismatch;

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await register({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        marketing_opt_in: marketingOptIn
      });
      navigate(lp('verify-email'));
    } catch (err) {
      // Handle structured validation errors from backend
      if (err.response?.data?.errors) {
        const backendErrors = {};
        err.response.data.errors.forEach(e => { backendErrors[e.field] = e.message; });
        setFieldErrors(backendErrors);
      } else {
        setError(err.response?.data?.error || t.auth.register.registrationFailed);
      }
    } finally { setLoading(false); }
  };

  const fields = [
    [['first_name', t.auth.register.firstNameLabel, 'text'], ['last_name', t.auth.register.lastNameLabel, 'text']],
    [['email', t.auth.register.emailLabel, 'email']],
    [['phone', t.auth.register.phoneLabel, 'tel']],
    [['password', t.auth.register.passwordLabel, 'password'], ['confirm', t.auth.register.confirmLabel, 'password']],
  ];

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', paddingTop: '7rem' }}>
      <SEOHead page="register" />
      <div style={{ width: '100%', maxWidth: '560px' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '0.3rem' }}>CJPA</div>
          <div style={{ fontSize: '0.58rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase' }}>Mécanique Auto</div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.2rem', color: '#E8ECF0', fontWeight: 300, marginTop: '2rem' }}>{t.auth.register.title}</h1>
          <p style={{ color: '#94A3B8', fontSize: '0.78rem', marginTop: '0.5rem' }}>{mc('register', 'subtitle', t.auth.register.subtitle)}</p>
        </div>

        <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
          {error && <div style={{ background: 'rgba(180,50,50,0.1)', border: '1px solid rgba(180,50,50,0.3)', color: '#e08080', padding: '0.8rem 1rem', fontSize: '0.78rem', marginBottom: '1.5rem' }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            {fields.map((row, ri) => (
              <div key={ri} className="cjpa-form-2col" style={{ marginBottom: '1.2rem' }}>
                {row.map(([field, label, type]) => (
                  <div key={field}>
                    <label style={{ display: 'block', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{label}</label>
                    <input type={type} value={form[field]} onChange={e => setForm({...form, [field]: e.target.value})}
                      required={field !== 'phone'}
                      maxLength={field === 'email' ? 255 : field === 'first_name' || field === 'last_name' ? 50 : field === 'password' || field === 'confirm' ? 128 : 20}
                      style={{
                        width: '100%', background: '#1a2332',
                        border: `1px solid ${fieldErrors[field] ? 'rgba(212,43,43,0.6)' : 'rgba(148,163,184,0.2)'}`,
                        color: '#E8ECF0', padding: '0.8rem', fontSize: '0.82rem',
                        fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box',
                      }}
                      onFocus={e => e.target.style.borderColor = '#D42B2B'}
                      onBlur={e => e.target.style.borderColor = fieldErrors[field] ? 'rgba(212,43,43,0.6)' : 'rgba(148,163,184,0.2)'}
                    />
                    {fieldErrors[field] && (
                      <div style={{ color: '#e08080', fontSize: '0.72rem', marginTop: '0.3rem' }}>{fieldErrors[field]}</div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Consent section */}
            <div style={{ borderTop: '1px solid rgba(148,163,184,0.1)', paddingTop: '1.2rem', marginTop: '0.5rem', marginBottom: '1.2rem' }}>
              {/* Transactional notice */}
              <div style={{ fontSize: '0.72rem', color: '#94A3B8', marginBottom: '1rem', lineHeight: '1.5' }}>
                <strong style={{ color: '#E8ECF0' }}>{t.auth.register.consent.transactionalTitle}</strong>{' '}
                {t.auth.register.consent.transactionalDesc}
              </div>

              {/* Marketing opt-in checkbox */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer', marginBottom: '0.6rem' }}>
                <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)}
                  style={{ marginTop: '0.2rem', accentColor: '#D42B2B', width: '16px', height: '16px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.72rem', color: '#E8ECF0', lineHeight: '1.5' }}>
                  {t.auth.register.consent.marketingLabel}
                </span>
              </label>

              <div style={{ fontSize: '0.65rem', color: '#64748B', lineHeight: '1.4' }}>
                {t.auth.register.consent.marketingHint}{' '}
                <Link to={lp('privacy')} style={{ color: '#D42B2B', textDecoration: 'none' }}>{t.auth.register.consent.privacyLink}</Link>
              </div>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', background: '#D42B2B', color: '#0C1220', border: 'none',
              padding: '1rem', fontFamily: "'Barlow', sans-serif", fontSize: '0.65rem',
              letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, marginTop: '0.5rem',
            }}>{loading ? t.auth.register.creating : t.auth.register.button}</button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(148,163,184,0.1)' }}>
            <span style={{ color: '#94A3B8', fontSize: '0.78rem' }}>{t.auth.register.hasAccount + ' '}</span>
            <Link to={lp('login')} style={{ color: '#D42B2B', fontSize: '0.78rem', textDecoration: 'none' }}>{t.auth.register.signIn}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
